// ...imports (same as before)
import {
  Box,
  FormControl,
  FormLabel,
  HStack,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Icon,
} from "@chakra-ui/react";
import { EditIcon } from "@chakra-ui/icons";
import { BrandButton } from "../BrandButton/BrandButton";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Editor, useMonaco } from "@monaco-editor/react";
import { Node } from "@xyflow/react";

import { DropDown } from "../Select/DropDown";
import { fetchAllProcesses } from "../../services/state/processes/processes-async-calls";
import { fetchProcessVaraiblesbyProcessId } from "../../services/state/Process-modular/processModular-async-calls";
import { AppDispatch, RootState } from "@/services/state/store";
import { Process } from "@/services/state/processes/processes-interface";
import { DataType } from "@/services/state/custom-data-types/customDataTypes-interface";
import { ProcessVariable } from "@/services/state/Process-modular/ProcessVaraible-interface";

export const SubProcessProperties = ({
  selectedNode,
  selectedSubProcess,
  setSelectedSubProcess,
  selectedSubProcessVariables,
  setSelectedSubProcessVariables,
  selectedSubProcessOutputs,
  setSelectedSubProcessOutputs,
  selectedSubProcessOutputsNames,
  setSelectedSubProcessOutputsNames,
  selectedSubProcessIsExpression,
  setSelectedSubProcessIsExpression,
  selectedSubProcessExpression,
  setSelectedSubProcessExpression,
  selectedSubProcessInputExpression,
  setSelectedSubProcessInputExpression,
  subProcessRunAs,
  setSubProcessRunAs,
  defineGroovyLanguage,
  processModelMatchers,
  ruleMatchers,
  pvMatchers,
  dataTypes,
}: {
  selectedNode: Node;
  selectedSubProcess: Process | null;
  setSelectedSubProcess: (value: Process | null) => void;
  selectedSubProcessVariables: {
    inputVar: string | null;
    expression: string;
    isDynamic: boolean;
  }[];
  setSelectedSubProcessVariables: React.Dispatch<
    React.SetStateAction<
      {
        inputVar: string | null;
        expression: string;
        isDynamic: boolean;
      }[]
    >
  >;
  selectedSubProcessOutputs: string[];
  setSelectedSubProcessOutputs: React.Dispatch<React.SetStateAction<string[]>>;
  selectedSubProcessOutputsNames: string[];
  setSelectedSubProcessOutputsNames: React.Dispatch<
    React.SetStateAction<string[]>
  >;
  selectedSubProcessIsExpression: boolean;
  setSelectedSubProcessIsExpression: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  selectedSubProcessExpression: string | null;
  setSelectedSubProcessExpression: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  selectedSubProcessInputExpression: string | null;
  setSelectedSubProcessInputExpression: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  subProcessRunAs: string;
  setSubProcessRunAs: React.Dispatch<React.SetStateAction<string>>;
  defineGroovyLanguage: (
    monacoInstance: any,
    ruleMatchers: string[],
    pvMatchers: string[],
    processModelMatchers: string[]
  ) => void;
  processModelMatchers: string[];
  ruleMatchers: string[];
  pvMatchers: string[];
  dataTypes: DataType[];
}) => {
  const dispatch: AppDispatch = useDispatch();
  const selectedProcess = useSelector(
    (state: RootState) => state.processes.selectedProcess
  );
  const process = useSelector((state: RootState) => state.processes.processes);
  const [processVariables, setProcessVariables] = useState<ProcessVariable[]>(
    []
  );
  const monaco = useMonaco();

  // Load all processes
  useEffect(() => {
    dispatch(fetchAllProcesses());
  }, []);

  // Fetch process variables for the selected subprocess
  useEffect(() => {
    if (selectedSubProcess) {
      dispatch(fetchProcessVaraiblesbyProcessId(selectedSubProcess.uuid))
        .unwrap()
        .then((response: ProcessVariable[]) => {
          setProcessVariables(response);
        })
        .catch((error: any) => {
          console.error("Error fetching process variables:", error);
        });
    }
  }, [selectedSubProcess]);

  // Register Groovy language with Monaco
  useEffect(() => {
    if (monaco) {
      defineGroovyLanguage(
        monaco,
        ruleMatchers,
        pvMatchers,
        processModelMatchers
      );
    }
  }, [monaco]);

  // Load data from selected node
  useEffect(() => {
    const details = selectedNode?.data?.subProcessDetails as
      | {
          subProcess?: string;
          expression?: string | null;
          isDynamic?: boolean;
          runAs?: string;
          subProcessVariables?: {
            inputVar: string | null;
            expression: string;
            isDynamic: boolean;
          }[];
          subProcessInputs?: string;
          subProcessOutputs?: string[];
        }
      | undefined;

    if (!details) return;

    const {
      subProcess,
      expression,
      isDynamic,
      runAs,
      subProcessVariables = [],
      subProcessInputs,
      subProcessOutputs = [],
    } = details;

    setSubProcessRunAs(runAs || "SYNC");

    if (isDynamic) {
      setSelectedSubProcessIsExpression(true);
      setSelectedSubProcessExpression(expression || "exp!(return '')");
      setSelectedSubProcessInputExpression(
        subProcessInputs || "exp!(return [:])"
      );
    } else {
      setSelectedSubProcessIsExpression(false);
      setSelectedSubProcess(process.find((p) => p.uuid === subProcess) || null);
    }

    setSelectedSubProcessVariables(subProcessVariables);
    setSelectedSubProcessOutputs(subProcessOutputs);
    setSelectedSubProcessOutputsNames([]);
  }, [selectedNode, process]);

  // Fill in missing selectedSubProcessVariables in non-expression mode
  useEffect(() => {
    if (!selectedSubProcessIsExpression && processVariables.length > 0) {
      const updatedVars = processVariables
        .filter((pv) => pv.isParam && pv.isActive === "Y")
        .map((pv) => {
          const existing = selectedSubProcessVariables.find(
            (item) => item.inputVar === pv.varNm
          );
          return (
            existing || {
              inputVar: pv.varNm,
              expression:
                pv.dataType.dataTypCd === "STR"
                  ? `exp!(return "${pv.varDefVal}")`
                  : `exp!(return ${pv.varDefVal})`,
              isDynamic: false,
            }
          );
        });

      setSelectedSubProcessVariables(updatedVars);
    }
  }, [selectedSubProcessIsExpression, processVariables]);

  // Resolve output names
  useEffect(() => {
    selectedSubProcessOutputs.forEach((output) => {
      const variable = processVariables.find((pv) => pv.uuid === output);
      if (
        variable &&
        !selectedSubProcessOutputsNames.includes(variable.varNm)
      ) {
        setSelectedSubProcessOutputsNames((prev) => [...prev, variable.varNm]);
      }
    });
  }, [selectedSubProcessOutputs, processVariables]);

  const toggleExpressionMode = () => {
    setSelectedSubProcessIsExpression(!selectedSubProcessIsExpression);
    if (!selectedSubProcessIsExpression) {
      setSelectedSubProcessExpression("exp!(return '')");
      setSelectedSubProcessInputExpression("exp!(return [:])");
    } else {
      setSelectedSubProcessExpression("");
      setSelectedSubProcessInputExpression("");
      setSelectedSubProcess(null);
    }
  };

  const editorOptions = {
    selectOnLineNumbers: true,
    minimap: { enabled: false },
    readOnly: false,
  };

  const navigateToSubProcess = () => {
    if (selectedSubProcess?.uuid) {
      window.open(
        `/platform/design/process/${selectedSubProcess.uuid}`,
        "_blank",
        "noopener,noreferrer"
      );
    }
  };

  return (
    <Box>
      {/* PROCESS SELECTION */}
      <HStack justify="space-between" align="center" mb={3}>
        <Text fontSize="18px" fontWeight="500">
          Process:
        </Text>
        <HStack spacing={2}>
          <Icon
            as={EditIcon}
            onClick={toggleExpressionMode}
            color="brand.500"
            cursor="pointer"
            boxSize={5}
          />
          {!selectedSubProcessIsExpression && selectedSubProcess && (
            <BrandButton
              size="xs"
              onClick={navigateToSubProcess}
              title="Navigate to subprocess"
            >
              Navigate
            </BrandButton>
          )}
        </HStack>
      </HStack>

      <FormControl>
        {selectedSubProcessIsExpression ? (
          <Box borderWidth="1px" borderRadius="md" overflow="hidden">
            <Editor
              value={selectedSubProcessExpression || ""}
              defaultLanguage="groovy"
              theme="vs-dark"
              height="10vh"
              width="100%"
              options={editorOptions}
              onChange={(e) => e && setSelectedSubProcessExpression(e)}
            />
          </Box>
        ) : (
          <DropDown
            selected={{
              id: selectedSubProcess?.uuid || "",
              name: selectedSubProcess?.processNm || "",
              component: <Text>{selectedSubProcess?.processNm || ""}</Text>,
            }}
            options={process
              .filter((p) => p.uuid !== selectedProcess?.uuid)
              .map((p) => ({
                id: p.uuid,
                name: p.processNm,
                component: <Text>{p.processNm}</Text>,
              }))}
            title={<Text />}
            onOptionChange={(option) => {
              if (!Array.isArray(option)) {
                setSelectedSubProcess(
                  process.find((p) => p.uuid === option.id) || null
                );
              }
            }}
          />
        )}
      </FormControl>

      {/* MODE SELECTION */}
      <Text fontSize="18px" fontWeight="500" my={3}>
        Mode:
      </Text>
      <RadioGroup value={subProcessRunAs} onChange={setSubProcessRunAs}>
        <Stack spacing={5} direction="row">
          <Radio colorScheme="brand" value="SYNC">
            Synchronous
          </Radio>
          <Radio colorScheme="brand" value="ASYNC">
            Asynchronous
          </Radio>
        </Stack>
      </RadioGroup>

      {/* INPUTS */}
      {(processVariables.length > 0 || selectedSubProcessIsExpression) && (
        <>
          <Text fontSize="18px" fontWeight="500" mt={6} mb={3}>
            Inputs:
          </Text>
          {selectedSubProcessIsExpression ? (
            <FormControl mt={3}>
              <FormLabel>
                Input Expression (expecting all inputs as map):
              </FormLabel>
              <Box borderWidth="1px" borderRadius="md" overflow="hidden">
                <Editor
                  value={selectedSubProcessInputExpression || ""}
                  defaultLanguage="groovy"
                  theme="vs-dark"
                  height="15vh"
                  width="100%"
                  options={editorOptions}
                  onChange={(e) => e && setSelectedSubProcessInputExpression(e)}
                />
              </Box>
            </FormControl>
          ) : (
            processVariables
              .filter((pv) => pv.isParam && pv.isActive === "Y")
              .map((pv, index) => (
                <FormControl key={index} mt={3}>
                  <HStack>
                    <HStack width="30%" gap={3}>
                      <FormLabel width="50%">{pv.varNm}</FormLabel>
                      <FormLabel width="50%">{pv.dataType.dataTypNm}</FormLabel>
                    </HStack>
                    <Box width="70%" borderWidth="1px" borderRadius="md" overflow="hidden">
                      <Editor
                        value={
                          selectedSubProcessVariables.find(
                            (v) => v.inputVar === pv.varNm
                          )?.expression
                        }
                        defaultLanguage="groovy"
                        theme="vs-dark"
                        height="10vh"
                        width="100%"
                        options={editorOptions}
                        onChange={(e) => {
                          if (e) {
                            const copy = [...selectedSubProcessVariables];
                            const i = copy.findIndex(
                              (v) => v.inputVar === pv.varNm
                            );
                            if (i !== -1) {
                              // Replace the entire object with a new one
                              copy[i] = {
                                ...copy[i],
                                expression: e,
                              };
                              setSelectedSubProcessVariables(copy);
                            }
                          }
                        }}
                      />
                    </Box>
                  </HStack>
                </FormControl>
              ))
          )}
        </>
      )}

      {/* OUTPUTS */}
      {!(selectedSubProcessIsExpression || subProcessRunAs === "ASYNC") && (
        <>
          <Text fontSize="18px" fontWeight="500" mt={3}>
            Outputs:
          </Text>
          <FormControl mt={3}>
            <DropDown
              selected={processVariables
                .filter((pv) => selectedSubProcessOutputs.includes(pv.uuid))
                .map((pv) => ({
                  id: pv.uuid,
                  name: pv.varNm,
                  component: <Text>{pv.varNm}</Text>,
                }))}
              options={processVariables
                .filter((pv) => pv.isActive === "Y")
                .map((pv) => ({
                  id: pv.uuid,
                  name: pv.varNm,
                  component: <Text>{pv.varNm}</Text>,
                }))}
              title={<Text />}
              onOptionChange={(option) => {
                if (Array.isArray(option)) {
                  setSelectedSubProcessOutputs(option.map((opt) => opt.id));
                }
              }}
              isMulti
            />
          </FormControl>
        </>
      )}
    </Box>
  );
};
